import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { OpeningSide } from "building-types/openings/openingTypes";
import type {
  HeadCasingArchedSlices,
  HeadCasingEyebrowTrim,
  HeadCasingFullArch,
  HeadCasingJackArch,
  HeadCasingSettings,
  HeadCasingSlices,
  HeadCasingStone,
  HeadCasingTrim,
  KeystoneOptions,
} from "building-types/settings/headCasingSettings";
import { HeadCasingType } from "building-types/settings/settingsTypesEnum";
import type {
  ExteriorDoorCasingViewNoteTable,
  ExteriorWindowCasingViewNoteTable,
} from "building-types/settings/viewNotesSettings";
import { ExteriorDoorCasingViewNoteType } from "building-types/settings/viewNotesSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import uid from "common/utils/uid";
import { exteriorMaterialOverrideNew } from "doc/utils/materialTools";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { arc2ByCenterStartAngle } from "geom/arc";
import type { BRep, Extrusion } from "geom/brep";
import { faceNew } from "geom/brep";
import { REMOVE_COPLANAR_LINES_EPSILON } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongNormal, extrudeFaceAlongPolyLine3 } from "geom/extrude";
import type GeomTagGroup from "geom/geomTagGroup";
import { line2Len } from "geom/line2";
import { offsetPolyLine2, polyLine2Thicken } from "geom/offset";
import { planeCopy, planeMoveAlongNormal, planeNormal, polyLine2OnPlane } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2ConcatAll, polyLine2ExtendEnd, polyLine2ExtendStart, polyLine2Reverse } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { rect2ByTwoPoints } from "geom/rect2";
import { UVGeneration } from "geom/uv";
import { vec3Scale } from "geom/vec3";
import { headCasingStoneProfile, jackArchLeftRightProfiles, keystoneProfile } from "./casing/casingProfiles";
import { headCasingExtension, headCasingRemoveCorner } from "./casing/casingQuery";

function makeKeystone(bottomLine: PolyLine2, doorPlane: Plane, keystone: KeystoneOptions): Extrusion {
  const keystoneOutline = keystoneProfile(bottomLine, keystone);
  const keystoneFace = faceNew([keystoneOutline], doorPlane);
  return extrudeFaceAlongNormal(keystoneFace, keystone.keystoneDepth);
}

function arcRadiusByChordLengthHeight(chordLength: number, arcHeight: number) {
  const z1 = Math.pow(chordLength, 2);
  const z2 = 8 * arcHeight;
  const z3 = arcHeight / 2;
  return z1 / z2 + z3;
}

function arcCentralAngleByChordLengthRadius(chordLength: number, radius: number) {
  const theta = Math.asin(chordLength / 2 / radius) * 2;
  return theta;
}

export type HeadCasingSettingsWithMaterial =
  | HeadCasingTrim
  | HeadCasingStone
  | HeadCasingJackArch
  | HeadCasingFullArch
  | HeadCasingEyebrowTrim;

export function headCasingMaterial(headCasing: HeadCasingSettingsWithMaterial): ExteriorMaterial {
  switch (headCasing.headCasingType) {
    case HeadCasingType.TRIM:
      return headCasing.material ?? "trimExterior0";
    case HeadCasingType.STONE:
      return headCasing.material ?? "headCasing1";
    case HeadCasingType.JACK_ARCH:
      return headCasing.material ?? "headCasing0";
    case HeadCasingType.FULL_ARCH:
      return headCasing.material;
    case HeadCasingType.EYEBROW_TRIM:
      return headCasing.material;
  }
}

export function addHeadCasing({
  profiles,
  bottomLine,
  apronLine,
  arch,
  casingPlane,
  openingPlane,
  wallWidth,
  settings,
  extrusions,
  viewNotes,
  exportComponentName,
}: {
  profiles: OpeningSide;
  bottomLine: PolyLine2;
  apronLine: PolyLine2;
  arch: PolyLine2 | undefined;
  casingPlane: Plane;
  openingPlane: Plane;
  wallWidth: number;
  settings: HeadCasingSettings;
  extrusions: GeomTagGroup<ExteriorMaterialOverride>;
  viewNotes: ExteriorDoorCasingViewNoteTable | ExteriorWindowCasingViewNoteTable;
  exportComponentName?: string;
}): void {
  if (!settings) {
    return;
  }

  const headerDescriptor = viewEntityDescriptorNew(
    uid(),
    ViewEntityType.HEAD_CASING,
    viewNotes[ExteriorDoorCasingViewNoteType.HEAD], // same works for windows and doors
  );

  switch (settings.headCasingType) {
    case HeadCasingType.SLICES:
      return addHeadCasingSiding(
        profiles,
        bottomLine,
        openingPlane,
        settings,
        extrusions,
        headerDescriptor,
        exportComponentName,
      );
    case HeadCasingType.STONE:
      return addHeadCasingStone(
        profiles,
        bottomLine,
        openingPlane,
        settings,
        extrusions,
        headerDescriptor,
        exportComponentName,
      );
    case HeadCasingType.JACK_ARCH:
      return addHeadCasingJackArch(
        profiles,
        bottomLine,
        openingPlane,
        settings,
        extrusions,
        headerDescriptor,
        exportComponentName,
      );
    case HeadCasingType.TRIM:
      return addHeadCasingTrim(
        profiles,
        bottomLine,
        arch,
        openingPlane,
        settings,
        extrusions,
        headerDescriptor,
        exportComponentName,
      );
    case HeadCasingType.FULL_ARCH:
      return addHeadCasingFullArch({
        profiles,
        topLine: arch,
        apronLine,
        casingPlane,
        wallWidth,
        trimOptions: settings,
        extrusions,
        descriptor: headerDescriptor,
        exportComponentName,
      });
    case HeadCasingType.ARCHED_SLICES:
      return addHeadCasingArchedSlices(
        profiles,
        bottomLine,
        openingPlane,
        settings,
        extrusions,
        headerDescriptor,
        exportComponentName,
      );
    case HeadCasingType.EYEBROW_TRIM:
      return addHeadCasingEyebrowTrim(
        profiles,
        bottomLine,
        openingPlane,
        settings,
        extrusions,
        headerDescriptor,
        exportComponentName,
      );
    case HeadCasingType.NONE:
    default:
      return;
  }
}

function addHeadCasingSiding(
  profiles: OpeningSide,
  bottomLine: PolyLine2,
  openingPlane: Plane,
  sidingOptions: HeadCasingSlices,
  extrusions: GeomTagGroup<ExteriorMaterialOverride>,
  descriptor: ViewEntityDescriptor,
  exportComponentName?: string,
) {
  if (!(sidingOptions)) {
    console.assert(false, "Head Casing course: options not defined!");
  }
  let currentDepth = 0;
  let currentHeight = 0;
  let currentWidthOffset = 0;
  for (const slice of sidingOptions.headCasingSlices || []) {
    const outline = polyLine2Reverse(
      rect2ByTwoPoints(
        bottomLine[0] - slice.widthOffset * 0.5 - currentWidthOffset,
        bottomLine[1] + currentHeight,
        bottomLine[2] + slice.widthOffset * 0.5 + currentWidthOffset,
        bottomLine[3] + currentHeight + slice.height,
      ),
    );
    profiles.headCasing.push(outline);

    currentHeight += slice.height;
    currentWidthOffset += slice.widthOffset * 0.5;
    currentDepth += slice.depthOffset;

    const stone = faceNew([outline], openingPlane);
    extrusions.add(
      extrudeFaceAlongNormal(stone, currentDepth),
      exteriorMaterialOverrideNew(
        slice.material ?? "headCasing0",
        undefined,
        descriptor,
        undefined,
        exportComponentName,
      ),
    );
  }

  if (sidingOptions.includeKeystone) {
    extrusions.add(
      makeKeystone(bottomLine, openingPlane, sidingOptions),
      exteriorMaterialOverrideNew(
        sidingOptions.keystoneMaterial ?? "headCasing1",
        undefined,
        descriptor,
        undefined,
        exportComponentName,
      ),
    );
  }

  profiles.headHeight = currentHeight;
}

function addHeadCasingStone(
  profiles: OpeningSide,
  bottomLine: PolyLine2,
  openingPlane: Plane,
  stoneOptions: HeadCasingStone,
  extrusions: GeomTagGroup<ExteriorMaterialOverride>,
  descriptor: ViewEntityDescriptor,
  exportComponentName?: string,
) {
  if (!(stoneOptions)) {
    console.assert(false, "Head Casing course: options not defined!");
  }
  const outline = headCasingStoneProfile(bottomLine, stoneOptions);
  profiles.headCasing.push(outline);

  const stone = faceNew([outline], openingPlane);
  extrusions.add(
    extrudeFaceAlongNormal(stone, stoneOptions.headCasingDepth),
    exteriorMaterialOverrideNew(
      headCasingMaterial(stoneOptions),
      undefined,
      descriptor,
      undefined,
      exportComponentName,
    ),
  );

  profiles.headHeight = stoneOptions.headCasingHeight;
}

function addHeadCasingTrim(
  profiles: OpeningSide,
  bottomLine: PolyLine2,
  arch: PolyLine2 | undefined,
  openingPlane: Plane,
  trimOptions: HeadCasingTrim,
  extrusions: GeomTagGroup<ExteriorMaterialOverride>,
  descriptor: ViewEntityDescriptor,
  exportComponentName?: string,
): void {
  if (!(trimOptions)) {
    console.assert(false, "Head Casing course: options not defined!");
  }
  const { headCasingHeight, headCasingDepth } = trimOptions;
  const extension = headCasingExtension(trimOptions);
  const adjustedHeadCasingDepth = headCasingDepth + REMOVE_COPLANAR_LINES_EPSILON;

  let trimExtrusion: Extrusion | BRep | undefined = undefined;
  if (arch) {
    const [nX, nY, nZ] = planeNormal(openingPlane);
    const trimProfile = polyLine2New([
      0,
      0,
      0,
      adjustedHeadCasingDepth,
      headCasingHeight,
      adjustedHeadCasingDepth,
      headCasingHeight,
      0,
      0,
      0,
    ]);

    const profile = polyLine2Thicken(arch, headCasingHeight);
    profiles.headCasing.push(profile);

    trimExtrusion = extrudeFaceAlongPolyLine3([trimProfile], polyLine2OnPlane(openingPlane, arch), nX, nY, nZ);
  } else {
    if (bottomLine.length === 4) {
      if (!headCasingRemoveCorner(trimOptions)) {
        polyLine2ExtendStart(bottomLine, extension ?? 0);
        polyLine2ExtendEnd(bottomLine, extension ?? 0);

        const outline = polyLine2Reverse(
          polyLine2New([
            bottomLine[0],
            bottomLine[1],
            bottomLine[2],
            bottomLine[3],
            bottomLine[2],
            bottomLine[3] + headCasingHeight,
            bottomLine[0],
            bottomLine[1] + headCasingHeight,
            bottomLine[0],
            bottomLine[1],
          ]),
        );

        const trim = faceNew([outline], openingPlane);
        trimExtrusion = extrudeFaceAlongNormal(trim, adjustedHeadCasingDepth + DIST_EPSILON);

        profiles.headCasing.push(outline);
      } else {
        const outline = polyLine2Reverse(
          polyLine2New([
            bottomLine[0] - extension,
            bottomLine[1],
            bottomLine[0],
            bottomLine[1],
            bottomLine[2],
            bottomLine[3],
            bottomLine[2] + extension,
            bottomLine[3],
            bottomLine[2] + extension,
            bottomLine[3] + headCasingHeight,
            bottomLine[2],
            bottomLine[3] + headCasingHeight,
            bottomLine[0],
            bottomLine[1] + headCasingHeight,
            bottomLine[0] - extension,
            bottomLine[1] + headCasingHeight,
            bottomLine[0] - extension,
            bottomLine[1],
          ]),
        );

        const trim = faceNew([outline], openingPlane);
        trimExtrusion = extrudeFaceAlongNormal(trim, adjustedHeadCasingDepth + DIST_EPSILON);

        profiles.headCasing.push(outline);
      }
    } else {
      if (bottomLine.length === 6) {
        if (!headCasingRemoveCorner(trimOptions)) {
          polyLine2ExtendStart(bottomLine, extension ?? 0);
          polyLine2ExtendEnd(bottomLine, extension ?? 0);

          const outline = polyLine2Reverse(
            polyLine2New([
              bottomLine[0],
              bottomLine[1],
              bottomLine[2],
              bottomLine[3],
              bottomLine[4],
              bottomLine[5],
              bottomLine[4],
              bottomLine[5] + headCasingHeight,
              bottomLine[2],
              bottomLine[3] + headCasingHeight,
              bottomLine[0],
              bottomLine[1] + headCasingHeight,
              bottomLine[0],
              bottomLine[1],
            ]),
          );

          const trim = faceNew([outline], openingPlane);
          trimExtrusion = extrudeFaceAlongNormal(trim, adjustedHeadCasingDepth);

          profiles.headCasing.push(outline);
        } else {
          const outline = polyLine2Reverse(
            polyLine2New([
              bottomLine[0] - extension,
              bottomLine[1],
              bottomLine[0],
              bottomLine[1],
              bottomLine[2],
              bottomLine[3],
              bottomLine[4],
              bottomLine[5],
              bottomLine[4] + extension,
              bottomLine[5],
              bottomLine[4] + extension,
              bottomLine[5] + headCasingHeight,
              bottomLine[4],
              bottomLine[5] + headCasingHeight,
              bottomLine[2],
              bottomLine[3] + headCasingHeight,
              bottomLine[0],
              bottomLine[1] + headCasingHeight,
              bottomLine[0] - extension,
              bottomLine[1] + headCasingHeight,
              bottomLine[0] - extension,
              bottomLine[1],
            ]),
          );

          const trim = faceNew([outline], openingPlane);
          trimExtrusion = extrudeFaceAlongNormal(trim, adjustedHeadCasingDepth);

          profiles.headCasing.push(outline);
        }
      }
    }
  }

  if (trimExtrusion) {
    extrusions.add(
      trimExtrusion,
      exteriorMaterialOverrideNew(
        headCasingMaterial(trimOptions),
        undefined,
        descriptor,
        undefined,
        exportComponentName,
      ),
    );
  }
  profiles.headHeight = headCasingHeight;
}

function addHeadCasingJackArch(
  profiles: OpeningSide,
  bottomLine: PolyLine2,
  doorPlane: Plane,
  jackArchOptions: HeadCasingJackArch,
  extrusions: GeomTagGroup<ExteriorMaterialOverride>,
  descriptor: ViewEntityDescriptor,
  exportComponentName?: string,
): void {
  if (!(jackArchOptions)) {
    console.assert(false, "Head Casing course: options not defined!");
  }

  const { bottomLineExt, leftOutline, rightOutline, leftPlane, rightPlane } = jackArchLeftRightProfiles(
    bottomLine,
    doorPlane,
    jackArchOptions,
  );

  // build the extrusions
  const leftFace = faceNew([leftOutline], leftPlane);
  const rightFace = faceNew([rightOutline], rightPlane);

  extrusions.add(
    extrudeFaceAlongNormal(leftFace, jackArchOptions.headCasingDepth),
    exteriorMaterialOverrideNew(
      headCasingMaterial(jackArchOptions),
      undefined,
      descriptor,
      undefined,
      exportComponentName,
    ),
  );
  extrusions.add(
    extrudeFaceAlongNormal(rightFace, jackArchOptions.headCasingDepth),
    exteriorMaterialOverrideNew(
      headCasingMaterial(jackArchOptions),
      undefined,
      descriptor,
      undefined,
      exportComponentName,
    ),
  );

  if (jackArchOptions.includeKeystone) {
    extrusions.add(
      makeKeystone(bottomLineExt, doorPlane, jackArchOptions),
      exteriorMaterialOverrideNew(
        jackArchOptions.keystoneMaterial ?? "headCasing1",
        undefined,
        descriptor,
        undefined,
        exportComponentName,
      ),
    );
  }

  profiles.headHeight = Math.max(jackArchOptions.keystoneHeight, jackArchOptions.headCasingHeight);
}

function addHeadCasingFullArch({
  profiles,
  topLine,
  apronLine,
  casingPlane,
  wallWidth,
  trimOptions,
  extrusions,
  descriptor,
  exportComponentName,
}: {
  profiles: OpeningSide;
  topLine: PolyLine2 | undefined;
  apronLine: PolyLine2;
  casingPlane: Plane;
  wallWidth: number;
  trimOptions: HeadCasingFullArch;
  extrusions: GeomTagGroup<ExteriorMaterialOverride>;
  descriptor: ViewEntityDescriptor;
  exportComponentName?: string;
}): void {
  if (!topLine) {
    return;
  }

  if (!(trimOptions)) {
    console.assert(false, "Head Casing course: options not defined!");
  }
  const { headCasingHeight, headCasingDepth, material, invertUV } = trimOptions;
  const [nX, nY, nZ] = vec3Scale(...planeNormal(casingPlane), -1);
  const trimDepth = wallWidth + headCasingDepth * 2;
  const profile = polyLine2New([0, trimDepth, headCasingHeight, trimDepth, headCasingHeight, 0, 0, 0, 0, trimDepth]);

  // project ends straight down
  const fullArc = polyLine2ConcatAll(
    [[topLine[0], apronLine[1]], topLine, [topLine[topLine.length - 2], apronLine[3]]],
    false,
  );

  const trimPlane = planeMoveAlongNormal(planeCopy(casingPlane), headCasingDepth);
  // @TODO(Andrew): replace this with DIST_Z_FIGHT
  const trimArc = offsetPolyLine2(fullArc, 1);
  const onPlane = polyLine2OnPlane(trimPlane, trimArc);
  const extrusion = extrudeFaceAlongPolyLine3([profile], onPlane, nX, nY, nZ);

  extrusions.add(
    extrusion,
    exteriorMaterialOverrideNew(
      material,
      invertUV ? UVGeneration.INVERT : UVGeneration.NONE,
      descriptor,
      undefined,
      exportComponentName,
    ),
  );

  profiles.headHeight = trimOptions.headCasingHeight;
}

function addHeadCasingArchedSlices(
  profiles: OpeningSide,
  bottomLine: PolyLine2,
  openingPlane: Plane,
  trimOptions: HeadCasingArchedSlices,
  extrusions: GeomTagGroup<ExteriorMaterialOverride>,
  descriptor: ViewEntityDescriptor,
  exportComponentName?: string,
): void {
  //create base arch
  const chordLength = line2Len(bottomLine[0], bottomLine[1], bottomLine[2], bottomLine[3]);
  const arcHeight = trimOptions.headCasingArcHeight;
  const radius = arcRadiusByChordLengthHeight(chordLength, arcHeight);
  const centralAngle = arcCentralAngleByChordLengthRadius(chordLength, radius);
  const arcCenterX = (bottomLine[0] + bottomLine[2]) / 2;
  const arcCenterY = bottomLine[1] - radius + arcHeight;

  const bottomArc = arc2ByCenterStartAngle(
    arcCenterX,
    arcCenterY,
    bottomLine[0],
    bottomLine[1],
    -centralAngle,
    0.01,
    10,
    15,
  );
  let overallHeight = arcHeight;

  //interior geometry
  if (trimOptions.headCasingInteriorDepth > 0) {
    const profile = polyLine2New([...bottomArc, bottomLine[0], bottomLine[1]]);
    const face = faceNew([profile], openingPlane);
    extrusions.add(
      extrudeFaceAlongNormal(face, trimOptions.headCasingInteriorDepth),
      exteriorMaterialOverrideNew(
        trimOptions.headCasingInteriorMaterial ?? "headCasing0",
        undefined,
        descriptor,
        undefined,
        exportComponentName,
      ),
    );
  }

  const [nX, nY, nZ] = planeNormal(openingPlane);

  //arch slices
  let lastArc = bottomArc;
  for (const archSlice of trimOptions.headCasingArchedSlices || []) {
    const { height, depthOffset, material } = archSlice;
    const topArc = offsetPolyLine2(lastArc, -height);
    const profile = polyLine2New([0, 0, -height, 0, -height, depthOffset, 0, depthOffset, 0, 0]);
    extrusions.add(
      extrudeFaceAlongPolyLine3([profile], polyLine2OnPlane(openingPlane, lastArc), nX, nY, nZ),
      exteriorMaterialOverrideNew(material ?? "headCasing0", undefined, descriptor, undefined, exportComponentName),
    );
    lastArc = topArc;
    overallHeight += height;
  }

  profiles.headHeight = overallHeight;
}

function addHeadCasingEyebrowTrim(
  profiles: OpeningSide,
  bottomLine: PolyLine2,
  openingPlane: Plane,
  trimOptions: HeadCasingEyebrowTrim,
  extrusions: GeomTagGroup<ExteriorMaterialOverride>,
  descriptor: ViewEntityDescriptor,
  exportComponentName?: string,
): void {
  const arcHeight = trimOptions.headCasingArcHeight;
  const baseHeight = trimOptions.headCasingHeight - arcHeight;

  polyLine2ExtendStart(bottomLine, trimOptions.headCasingExtension ?? 0);
  polyLine2ExtendEnd(bottomLine, trimOptions.headCasingExtension ?? 0);

  const rect = polyLine2Reverse(
    rect2ByTwoPoints(bottomLine[0], bottomLine[1], bottomLine[2], bottomLine[3] + baseHeight),
  );

  const chordLength = line2Len(rect[2], rect[3], rect[4], rect[5]);
  const radius = arcRadiusByChordLengthHeight(chordLength, arcHeight);
  const centralAngleB = arcCentralAngleByChordLengthRadius(chordLength, radius);
  const arcCenterX = (rect[2] + rect[4]) / 2;
  const arcCenterY = rect[3] - radius + arcHeight;

  const arc = arc2ByCenterStartAngle(arcCenterX, arcCenterY, rect[2], rect[3], -centralAngleB);

  const profile = polyLine2New([rect[0], rect[1], rect[2], rect[3], ...arc, rect[6], rect[7], rect[0], rect[1]]);
  const border = faceNew([profile], openingPlane);
  const extrusion = extrudeFaceAlongNormal(border, trimOptions.headCasingDepth);
  extrusions.add(
    extrusion,
    exteriorMaterialOverrideNew(headCasingMaterial(trimOptions), undefined, descriptor, undefined, exportComponentName),
  );

  profiles.headHeight = trimOptions.headCasingHeight;

  profiles.headCasing.push(profile);
}
